/**
 * Presentation rules for the admin itinerary-import pages. Pure so the labels,
 * tones and file handling are pinned by tests rather than found wrong by eye.
 */
import type { AdminItineraryImportFile, AdminItineraryImportRow } from '@orbit/api-types'
import type { DropdownOptionDef } from '@components/Dropdown'
import type { PillTone } from '@components/Pill'
import { countLabel } from '@/utils/plural'

export type ImportStatus = AdminItineraryImportRow['status']

/** The list's status filter: one status, or every one. */
export type ImportStatusFilter = ImportStatus | 'all'

// Keyed by the wire type, so a status added to the enum fails the build here
// until it has a label and a tone (api-types can't be imported at runtime).
const STATUS_LABELS: Record<ImportStatus, string> = {
	Pending: 'Pending',
	Processing: 'Processing',
	Success: 'Success',
	NoEventsFound: 'No events found',
	Failed: 'Failed',
}

const STATUS_TONES: Record<ImportStatus, PillTone> = {
	Pending: 'muted',
	Processing: 'muted',
	Success: 'success',
	NoEventsFound: 'gold',
	Failed: 'danger',
}

export const statusLabel = (status: ImportStatus): string => STATUS_LABELS[status]

export const statusTone = (status: ImportStatus): PillTone => STATUS_TONES[status]

export const STATUS_FILTER_OPTIONS: ReadonlyArray<DropdownOptionDef<ImportStatusFilter>> = [
	{ value: 'all', label: 'All statuses' },
	...(Object.keys(STATUS_LABELS) as ImportStatus[]).map(status => ({
		value: status,
		label: STATUS_LABELS[status],
	})),
]

/** What the requester sent, in a few words: "2 files + text", "Text only", "1 file". */
export const sourceSummary = (row: Pick<AdminItineraryImportRow, 'fileCount' | 'hasText'>): string => {
	if (row.fileCount === 0) return row.hasText ? 'Text only' : 'Nothing'
	const files = countLabel(row.fileCount, 'file')
	return row.hasText ? `${files} + text` : files
}

export const importPath = (id: string): string => `/admin/itinerary-imports/${id}`

export type ImportFileKind = 'image' | 'pdf' | 'text' | 'other'

/**
 * How the detail page shows an uploaded file. HEIC/HEIF are images most browsers
 * can't draw, so they get a link like any other file rather than a broken thumbnail.
 */
export const fileKind = (contentType: string | null): ImportFileKind => {
	if (!contentType) return 'other'
	if (contentType === 'application/pdf') return 'pdf'
	if (contentType.startsWith('text/')) return 'text'
	if (contentType.startsWith('image/') && !/^image\/hei[cf]$/.test(contentType)) return 'image'
	return 'other'
}

/** A file's caption on the detail page: its position, then its type ("File 2 · PDF"). */
export const fileCaption = (file: Pick<AdminItineraryImportFile, 'name' | 'contentType'>, index: number): string => {
	const extension = file.name.includes('.') ? file.name.split('.').pop()?.toUpperCase() : undefined
	return extension ? `File ${index + 1} · ${extension}` : `File ${index + 1}`
}

/** The raw model response, pretty-printed for the collapsible block. */
export const formatRawResponse = (raw: unknown): string => JSON.stringify(raw, null, 2)

/** The created-events card title: how many the import made, and how many are gone since. */
export const createdEventsTitle = (events: ReadonlyArray<{ isDeleted: boolean }>): string => {
	const deleted = events.filter(event => event.isDeleted).length
	return deleted === 0
		? `Created events (${events.length})`
		: `Created events (${events.length} · ${deleted} deleted)`
}
